import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  IconButton,
  Modal,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import {
  canRequestModelFullKtx2Postprocess,
  createModelFullKtx2ClientRequestId,
  getModelFullKtx2Postprocess,
  isModelFullKtx2UnknownOutcomeError,
  MODEL_FULL_KTX2_POLL_INTERVAL_MS,
  requestModelFullKtx2Postprocess,
} from './model-full-ktx2-postprocess';

const ModelFullKtx2PostprocessModal = ({
  calculationGuid,
  onClose,
  open,
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [errorMessageKey, setErrorMessageKey] = useState('');
  const [isOutcomeUnknown, setIsOutcomeUnknown] = useState(false);
  const pendingClientRequestIdRef = useRef(null);

  const refreshStatus = useCallback(async ({ preserveMessage = false, signal } = {}) => {
    setIsStatusLoading(true);

    try {
      const nextStatus = await getModelFullKtx2Postprocess({
        calculationGuid,
        signal,
      });

      setStatus(nextStatus);
      setIsOutcomeUnknown(false);
      if (!preserveMessage) {
        setErrorMessageKey('');
      }
      return nextStatus;
    } catch (error) {
      if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
        return undefined;
      }

      setErrorMessageKey(
        error?.messageKey ?? 'modelFullKtx2Postprocess.errors.statusFailed',
      );
      return undefined;
    } finally {
      setIsStatusLoading(false);
    }
  }, [calculationGuid]);

  useEffect(() => {
    if (!open || !calculationGuid) {
      return undefined;
    }

    const abortController = new AbortController();
    let timeoutId;

    const poll = async () => {
      await refreshStatus({
        signal: abortController.signal,
      });

      if (!abortController.signal.aborted) {
        timeoutId = window.setTimeout(poll, MODEL_FULL_KTX2_POLL_INTERVAL_MS);
      }
    };

    setStatus(null);
    setErrorMessageKey('');
    setIsOutcomeUnknown(false);
    pendingClientRequestIdRef.current = null;
    void poll();

    return () => {
      abortController.abort();
      window.clearTimeout(timeoutId);
    };
  }, [calculationGuid, open, refreshStatus]);

  const handleRequest = async () => {
    if (!canRequestModelFullKtx2Postprocess(status) || isOutcomeUnknown) {
      return;
    }

    const clientRequestId =
      pendingClientRequestIdRef.current ?? createModelFullKtx2ClientRequestId();
    pendingClientRequestIdRef.current = clientRequestId;
    setIsRequesting(true);
    setErrorMessageKey('');

    try {
      const nextStatus = await requestModelFullKtx2Postprocess({
        calculationGuid,
        clientRequestId,
        submissionId: status.submissionId ?? undefined,
      });

      pendingClientRequestIdRef.current = null;
      setStatus(nextStatus);
      setIsOutcomeUnknown(false);
    } catch (error) {
      if (isModelFullKtx2UnknownOutcomeError(error)) {
        setIsOutcomeUnknown(true);
        setErrorMessageKey('modelFullKtx2Postprocess.errors.unknownOutcome');
        const refreshedStatus = await refreshStatus({
          preserveMessage: true,
        });

        if (refreshedStatus !== undefined) {
          setErrorMessageKey('modelFullKtx2Postprocess.messages.refreshedAfterUnknown');
        }
      } else {
        setErrorMessageKey(
          error?.messageKey ?? 'modelFullKtx2Postprocess.errors.requestFailed',
        );
      }
    } finally {
      setIsRequesting(false);
    }
  };

  const retryEnabled =
    canRequestModelFullKtx2Postprocess(status) &&
    !isStatusLoading &&
    !isRequesting &&
    !isOutcomeUnknown;

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Fade in={open}>
        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: 5,
            maxWidth: 'calc(100vw - 32px)',
            p: 3,
            position: 'relative',
            width: 420,
          }}
        >
          <IconButton
            aria-label={t('modelFullKtx2Postprocess.buttons.close')}
            onClick={onClose}
            sx={{ color: '#999', position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" sx={{ mb: 2, pr: 4 }}>
            {t('modelFullKtx2Postprocess.title')}
          </Typography>

          {status === null && isStatusLoading ? (
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
              <CircularProgress size={22} />
              <Typography>{t('modelFullKtx2Postprocess.messages.loading')}</Typography>
            </Box>
          ) : (
            <>
              <Typography sx={{ mb: 1 }}>
                {t('modelFullKtx2Postprocess.labels.status')}{': '}
                {t(`modelFullKtx2Postprocess.statuses.${status?.status ?? 'unknown'}`)}
              </Typography>

              {status?.phase && (
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  {t('modelFullKtx2Postprocess.labels.phase')}{': '}{status.phase}
                </Typography>
              )}

              {status?.errorMessage && (
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  {status.errorMessage}
                </Typography>
              )}
            </>
          )}

          {errorMessageKey && (
            <Typography
              variant="body2"
              color={errorMessageKey.includes('.errors.') ? 'error' : 'text.secondary'}
              sx={{ mt: 1 }}
            >
              {t(errorMessageKey)}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
            <Button onClick={() => refreshStatus()} disabled={isStatusLoading || isRequesting}>
              {t('modelFullKtx2Postprocess.buttons.refresh')}
            </Button>
            <Button variant="contained" onClick={handleRequest} disabled={!retryEnabled}>
              {isRequesting
                ? t('modelFullKtx2Postprocess.buttons.requesting')
                : t('modelFullKtx2Postprocess.buttons.request')}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default ModelFullKtx2PostprocessModal;
